// Tab «Настройки» of a record (R-E7): the settings a record may hold its own value of, by groups. Each row says where its value comes
// from — the parent record («Как в проекте»), the whole account, or the record itself («Своё») — and «Вернуть …» links it back.

import { canWrite, resolveSetting, withOptions, scopeFor, settingChain, type Id, type MetaIndex, type RecordObj, type SettingDecl, type SettingValue } from '@confdeck/core-shared';
import { useAccess, useMeta, useRecord, useSync, useLinked } from '../sync/react.tsx';
import { PageBlock } from '../ui/Page.tsx';
import { Button, Field, useToast } from '../ui/primitives.tsx';
import { ValueControl } from './SettingsGroup.tsx';
import s from './settings.module.css';

/** The decls a record of this type holds values of. */
export const recordDecls = (decls: SettingDecl[] | undefined, typeId: Id | undefined): SettingDecl[] =>
  typeId ? (decls ?? []).filter((d) => !d.hidden && d.scopes.includes('record') && (d.records ?? []).includes(typeId)) : [];

/** The record's parent for settings, live: the record named by the type's `settingsParent` field. */
function useParent(rec: RecordObj | undefined): Id | null {
  const meta = useMeta();
  const p = rec ? meta.type(rec.typeId)?.settingsParent : undefined;
  const ids = useLinked(rec, p?.field ?? '');
  return p ? ids[0] ?? null : null;
}

/** The words of the source of a value, and of the button that returns to it. */
export function sourceWords(meta: MetaIndex, rec: RecordObj, parent: RecordObj | undefined): { same: string; back: string } {
  const p = meta.type(rec.typeId)?.settingsParent;
  const same = parent && p ? p.same : 'Как во всём аккаунте';
  return { same, back: `Вернуть: ${same.charAt(0).toLowerCase()}${same.slice(1)}` };
}

/** `groups`: only these groups (the tab «Сертификат» of an event shows its own), without the lead. */
export function RecordSettings({ recordId, groups: only }: { recordId: string; groups?: string[] }) {
  const client = useSync();
  const meta = useMeta();
  const rec = useRecord(recordId);
  const parentId = useParent(rec);
  const parent = useRecord(parentId);
  const grand = useParent(parent);
  const decls = recordDecls(client.header?.settings, rec?.typeId).filter((d) => !only || only.includes(d.group)).map((d) => withOptions(meta, d));
  if (!rec) return null;
  if (!decls.length) return <p className={s.lead}>У этой записи нет своих настроек.</p>;
  const chain = settingChain(rec.id, (x) => (x === rec.id ? parentId : x === parentId ? grand : null));
  const groups = [...new Set(decls.map((d) => d.group))];
  const words = sourceWords(meta, rec, parent);
  return (
    <section aria-label="Настройки записи">
      {!only && <p className={s.lead}>
        {parent ? `${words.same}, пока здесь не задано своё значение: смена там доходит сюда. Своё значение главнее, его не трогают правки «${parent.data[meta.type(parent.typeId)?.titleField ?? 'name'] ?? ''}».` : 'Пока здесь не задано своё значение, действует значение всего аккаунта из «Настроек».'}
      </p>}
      {groups.map((g) => (
        <PageBlock key={g} title={only ? undefined : g} rows>
          {decls.filter((d) => d.group === g).map((d) => <RecordRow key={d.key} d={d} rec={rec} chain={chain} words={words} />)}
        </PageBlock>
      ))}
    </section>
  );
}

function RecordRow({ d, rec, chain, words }: { d: SettingDecl; rec: RecordObj; chain: Id[]; words: { same: string; back: string } }) {
  const meta = useMeta();
  // The same rule as the server: values of a record are changed by those who may change the record (decision 041).
  const access = useAccess();
  const limit = access.all ? null : scopeFor(access, rec.typeId);
  const can = canWrite(access, rec.typeId) && (limit === null || (rec.typeId === access.scopeTypeId && limit.has(rec.id)));
  const client = useSync();
  const toast = useToast();
  const r = resolveSetting(meta, d, null, null, chain);
  const own = r.from === 'record' && r.def?.targetId === rec.id;
  const go = async (cmd: 'setting.set' | 'setting.clear', value?: SettingValue) => {
    const res = await client.run(cmd, { key: d.key, scope: 'record', targetId: rec.id, ...(cmd === 'setting.set' ? { value } : {}) });
    if (!res.ok) toast({ kind: 'error', text: res.error.message });
  };
  const hint = (
    <>
      {d.description}
      <span className={s.rowActions}>
        <span className={s.source} data-source={own ? 'own' : 'inherited'}>{own ? 'Своё' : words.same}</span>
        {own && can && <Button size="sm" variant="ghost" icon="undo" onClick={() => void go('setting.clear')}>{words.back}</Button>}
      </span>
    </>
  );
  return (
    <div className={s.setting} data-setting={d.key}>
      <Field as="div" inline="end" label={d.name} hint={hint}>
        <ValueControl d={d} value={r.value} disabled={!can} onChange={(v) => void go('setting.set', v)} />
      </Field>
    </div>
  );
}
